import Swal from "sweetalert2";

export const ACTION_FEEDBACK_EVENTS = {
    success: "sentry:action-feedback-success",
    error: "sentry:action-feedback-error",
    cancel: "sentry:action-feedback-cancel",
    confirmed: "sentry:action-feedback-confirmed",
};

export const ACTION_FEEDBACK_STORAGE_KEY = "action-feedback-success";
const PERSISTED_ACTION_FEEDBACK_TTL = 10000;

let actionSequence = 0;
const pendingActions = new Map();
const latestActionByType = new Map();
let navigationDelayPending = false;
let navigationDelayTimeout = null;

export const scheduleActionFeedbackNavigation = () => {
    navigationDelayPending = true;
    clearTimeout(navigationDelayTimeout);
    navigationDelayTimeout = setTimeout(() => {
        navigationDelayPending = false;
    }, 1500);
};

export const consumeActionFeedbackNavigation = () => {
    if (!navigationDelayPending) return false;

    navigationDelayPending = false;
    clearTimeout(navigationDelayTimeout);
    return true;
};

export const getActionFeedbackMessageType = (message) => {
    const text = String(message ?? "").toLowerCase();
    return /exclu|remov|cancelad/.test(text) ? "delete" : "save";
};

const getActiveAction = (type) => {
    const typedAction = pendingActions.get(latestActionByType.get(type));
    if (typedAction?.active) return typedAction;

    const activeActions = [...pendingActions.values()].filter((action) => action.active);
    return activeActions.length === 1 ? activeActions[0] : null;
};

export const persistActionFeedbackSuccess = (type, message) => {
    if (typeof window === "undefined") return false;

    try {
        const activeAction = getActiveAction(type);
        window.localStorage.setItem(ACTION_FEEDBACK_STORAGE_KEY, JSON.stringify({
            type,
            message,
            actionKey: activeAction?.actionKey ?? null,
            createdAt: Date.now(),
        }));
        return true;
    } catch {
        return false;
    }
};

export const consumePersistedActionFeedbackSuccess = (type, actionKey = null) => {
    if (typeof window === "undefined") return null;

    let storedValue;

    try {
        storedValue = window.localStorage.getItem(ACTION_FEEDBACK_STORAGE_KEY);
    } catch {
        return null;
    }

    if (!storedValue) return null;

    try {
        const feedback = JSON.parse(storedValue);
        const isExpired = !Number.isFinite(feedback.createdAt) || Date.now() - feedback.createdAt > PERSISTED_ACTION_FEEDBACK_TTL;

        if (isExpired) {
            window.localStorage.removeItem(ACTION_FEEDBACK_STORAGE_KEY);
            return null;
        }

        if (feedback.type !== type || typeof feedback.message !== "string") return null;

        const isMatchingAction = feedback.actionKey ? feedback.actionKey === actionKey : actionKey === "salvar";
        if (!isMatchingAction) return null;

        clearPersistedActionFeedbackSuccess();
        return feedback.message;
    } catch {
        clearPersistedActionFeedbackSuccess();
        return null;
    }
};

export const clearPersistedActionFeedbackSuccess = () => {
    if (typeof window === "undefined") return;

    try {
        window.localStorage.removeItem(ACTION_FEEDBACK_STORAGE_KEY);
    } catch {
        return;
    }
};

export const isPersistenceSuccessMessage = (message) => {
    const text = String(message ?? "").toLowerCase();
    return /(?:salv|exclu|adicion|cri|retific|invalid|enviad|assin|revisad|aprovad|reprovad|conclu|finaliz|cancel|ativad|inativad|desbloquead|bloquead|desvinc|vinculad|transfer|retirad|devolu|devolv|baix|entreg|gerad|importad|convert|atribu|assumid|encerrad|realizad|removid|agend|atualiz).*com sucesso/.test(text);
};

export const dispatchActionFeedback = (event, details = {}) => {
    if (typeof window === "undefined") return;

    const normalizedDetails = typeof details === "string" ? {type: details} : details;
    window.dispatchEvent(new CustomEvent(event, {detail: normalizedDetails}));
};

export const beginActionFeedback = (type, actionKey = null, active = true) => {
    if (!type) return null;

    const id = `action-feedback-${++actionSequence}`;
    pendingActions.set(id, {id, type, actionKey, active});
    latestActionByType.set(type, id);
    return id;
};

export const prepareActionFeedback = (type, actionKey) => beginActionFeedback(type, actionKey, false);

const activatePendingAction = (action) => {
    if (!action || action.active) return false;

    action.active = true;
    dispatchActionFeedback(ACTION_FEEDBACK_EVENTS.confirmed, {
        id: action.id,
        type: action.type,
    });
    return true;
};

export const activateActionFeedback = (type) => activatePendingAction(
    pendingActions.get(latestActionByType.get(type))
);

export const activateLatestPreparedAction = () => {
    const preparedActions = [...pendingActions.values()].filter((action) => !action.active);
    return activatePendingAction(preparedActions.at(-1));
};

const completePendingAction = (action, message) => {
    if (!action) return false;

    pendingActions.delete(action.id);
    if (latestActionByType.get(action.type) === action.id) latestActionByType.delete(action.type);
    dispatchActionFeedback(ACTION_FEEDBACK_EVENTS.success, {
        id: action.id,
        type: action.type,
        message,
    });
    return true;
};

export const completeActionFeedback = (type, message) => {
    return completePendingAction(getActiveAction(type), message);
};

export const completeActionFeedbackById = (id, message) => {
    return completePendingAction(pendingActions.get(id), message);
};

export const resetActionFeedbackById = (id, event = ACTION_FEEDBACK_EVENTS.error) => {
    const action = pendingActions.get(id);
    if (!action) return false;

    pendingActions.delete(action.id);
    if (latestActionByType.get(action.type) === action.id) latestActionByType.delete(action.type);
    dispatchActionFeedback(event, {id: action.id, type: action.type});
    return true;
};

export const resetActionFeedback = (type, event = ACTION_FEEDBACK_EVENTS.error) => {
    const actions = type ? [...pendingActions.values()].filter((action) => action.type === type) : [...pendingActions.values()];
    let reset = false;

    actions.filter(Boolean).forEach((action) => {
        pendingActions.delete(action.id);
        if (latestActionByType.get(action.type) === action.id) latestActionByType.delete(action.type);
        dispatchActionFeedback(event, {id: action.id, type: action.type});
        reset = true;
    });

    return reset;
};

export const resetActiveActionFeedback = (event = ACTION_FEEDBACK_EVENTS.error) => {
    const activeActions = [...pendingActions.values()].filter((action) => action.active);
    let reset = false;

    activeActions.forEach((action) => {
        pendingActions.delete(action.id);
        if (latestActionByType.get(action.type) === action.id) latestActionByType.delete(action.type);
        dispatchActionFeedback(event, {id: action.id, type: action.type});
        reset = true;
    });

    return reset;
};

export const attachToastFeedback = (toast) => {
    if (!toast || toast.__sentryActionFeedbackAttached) return;

    const toastSuccess = toast.success.bind(toast);
    const toastError = toast.error.bind(toast);

    toast.success = (message, ...args) => {
        const handled = isPersistenceSuccessMessage(message)
            && completeActionFeedback(getActionFeedbackMessageType(message), message);
        if (handled) scheduleActionFeedbackNavigation();
        return handled ? undefined : toastSuccess(message, ...args);
    };

    toast.error = (message, ...args) => {
        resetActionFeedback();
        return toastError(message, ...args);
    };

    toast.__sentryActionFeedbackAttached = true;
};

export const attachSwalFeedback = (swal) => {
    if (!swal || swal.__sentryActionFeedbackAttached) return;

    const swalFire = swal.fire.bind(swal);
    const swalShowValidationMessage = swal.showValidationMessage?.bind(swal);

    swal.fire = (options, ...args) => {
        const isError = options?.icon === "error" || options?.type === "error" || args.some((argument) => argument === "error");
        const isValidationAlert = !options?.showCancelButton && (options?.icon === "warning" || options?.type === "warning");

        if (isError || isValidationAlert) resetActionFeedback();

        return swalFire(options, ...args).then((result) => {
            if (options?.showCancelButton) {
                if (result?.isConfirmed) activateLatestPreparedAction();
                else resetActionFeedback(undefined, ACTION_FEEDBACK_EVENTS.cancel);
            }

            return result;
        });
    };

    if (swalShowValidationMessage) {
        swal.showValidationMessage = (message, ...args) => {
            resetActiveActionFeedback();
            return swalShowValidationMessage(message, ...args);
        };
    }

    swal.__sentryActionFeedbackAttached = true;
};

export const getActionFeedbackType = (actionFeedbackType = "none") => {
    return actionFeedbackType === "save" || actionFeedbackType === "delete" ? actionFeedbackType : null;
};

attachSwalFeedback(Swal);